import { Panel } from '@src/components/Layout/Components/Panel';
import { useFeatureFlag, useRoleAuth } from '@src/hooks';
import type { DealerSpaceParametersPanelConfig } from '@src/types/LayoutConfig';
import { ACTION, Box, ControlPrefix, DateRangePicker, FilterBuilder, FormControlSizes, Text } from '@talos/kyoko';
import type { LayoutPanelProps } from '../../types';
import { CustomerConfigurationCard } from './CustomerConfigurationCard';
import {
  DEALER_SPACE_DATE_RANGE_SHORTCUTS,
  useDealerSpaceParametersController,
} from './useDealerSpaceParametersController';

export function DealerSpaceParametersPanel({ api, params }: LayoutPanelProps<DealerSpaceParametersPanelConfig>) {
  const { isAuthorized } = useRoleAuth();
  const { showCustomerTiering } = useFeatureFlag();
  const {
    customerFilterBuilder,
    dateRangePickerValue,
    filterBuilder,
    handleDateRangeChanged,
    selectedCustomers,
    showCustomerSelector,
    showDateRangePicker,
  } = useDealerSpaceParametersController({
    controllerId: api.id,
    spaceParameterValues: params.spaceParameterValues,
    onSpaceParameterValuesChanged: values => api.updateParameters({ spaceParameterValues: values }),
  });
  const canViewCustomerConfigurationCard = isAuthorized(ACTION.VIEW_CUSTOMER_CONFIGURATIONS);

  return (
    <Panel panelApi={api} data-testid="dealer-space-parameters-panel" overflow="auto" p="spacing8">
      <Box mb="spacing16">
        <Text fontWeight="fontWeightBold" size="fontSizeXl" color="colorTextAttention">
          Filter this Space
        </Text>
        <Box mt="spacing4">
          <Text color="colorTextSecondary" size="fontSizeSmall">
            Filters selected here control compatible panels throughout this Space. Filters set within a panel apply only
            to that panel.
          </Text>
        </Box>
      </Box>
      {showDateRangePicker && (
        <Box mb="spacing8">
          <DateRangePicker
            data-testid="space-parameters-date-range-picker"
            value={dateRangePickerValue}
            onChange={handleDateRangeChanged}
            shortcuts={DEALER_SPACE_DATE_RANGE_SHORTCUTS}
            size={FormControlSizes.Small}
            prefix={<ControlPrefix>Date Range</ControlPrefix>}
            width="100%"
          />
        </Box>
      )}
      {showCustomerSelector && (
        <Box mb="spacing8">
          <FilterBuilder {...customerFilterBuilder} maxVisibleSelectionsPerProperty={1} />
        </Box>
      )}
      <FilterBuilder {...filterBuilder} maxVisibleSelectionsPerProperty={2} showClearFilters />
      {canViewCustomerConfigurationCard && (
        <CustomerConfigurationCard selectedCustomers={selectedCustomers} showCustomerTiering={showCustomerTiering} />
      )}
    </Panel>
  );
}
